import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Text,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
	Button,
	Flex,
	HStack,
	VStack,
	Icon,
	Center,
	Spinner,
} from "@chakra-ui/react";
import { FaCheck, FaArrowLeft, FaUndo } from "react-icons/fa";
import ListAnalysts from "components/analyst/ListAnalysts";
import AnalystDataAccessComponent from "components/analyst/AnalystDataAccessComponent";
import { AnalystProto, ScenarioAnalysis } from "build-ts/analyst";
import AnalystJobsComponent from "components/analyst/AnalystJobsComponent";
import { Header } from "build-ts/metadata";
import { getAnalyst, createAnalyst, updateAnalyst, refreshAnalystDataScope } from "services/AnalystService";
import { listScheduledJobs } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";
import { MetadataTypeEnum } from "build-ts/enums";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import lodash from "lodash";
import { useAnalystChat } from "context/AnalystChatContext";
import { BsRobot } from "react-icons/bs";
import { MdAnalytics } from "react-icons/md";
import { FaGear } from "react-icons/fa6";
import { IoChatbubble } from "react-icons/io5";

const AnalystPage: React.FC = () => {
	const [selectedAnalystData, setSelectedAnalystData] = useState<AnalystProto | undefined>(undefined);
	// store original analyst data for discarded changes
	const [originalAnalystData, setOriginalAnalystData] = useState<AnalystProto | undefined>(undefined);
	const [selectedAnalyst, setSelectedAnalyst] = useState<Header | null>(null);
	const [scenarios, setScenarios] = useState<ScenarioAnalysis[]>([]);
	const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
	const [isLoadingAnalystDetails, setIsLoadingAnalystDetails] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [isTabActive, setIsTabActive] = useState<number>(0);
	const [hasChanges, setHasChanges] = useState<boolean>(false);
	const [hasDiscardedChanges, setHasDiscardedChanges] = useState<boolean>(false);
	// State for job viewing/editing - used to control overlay button visibility
	const [isViewingOrEditingJob, setIsViewingOrEditingJob] = useState<boolean>(false);
	// state to track data scope changes
	const [hasDataScopeChanges, setHasDataScopeChanges] = useState<boolean>(false);
	// State for analyst jobs map
	const [analystJobsMap, setAnalystJobsMap] = useState<Map<string, Header[]>>(new Map());
	const [activeAnalystsMap, setActiveAnalystsMap] = useState<{
		analystId: string,
		active: boolean,
		noOfActiveJobs: number,
		noOfTotalJobs: number
	}[]>([]);
	const [isLoadingAllJobs, setIsLoadingAllJobs] = useState<boolean>(false);

	const { setAnalystsList, analystsList, isLoadingAnalysts } = useAnalystChat();

	const navigate = useNavigate();
	const { id, jobId } = useParams();
	const location = useLocation();

	// Use refs to store original individual pieces for change detection
	const originalDataRef = useRef<{
		name: string;
		description: string;
		// Track data scope
		dsInstructions: string;
		dsUseDefaultScope: boolean;
		dsMetrics: string[];
		dsBenchmarks: string[];
		dsColumnScopes: Array<{
			id: string | undefined;
			operator: number | undefined;
			values: string[] | undefined;
			is_hidden: boolean | undefined;
		}>;
		scenarios: ScenarioAnalysis[];
	}>({
		name: "",
		description: "",
		dsInstructions: "",
		dsUseDefaultScope: true,
		dsMetrics: [],
		dsBenchmarks: [],
		dsColumnScopes: [],
		scenarios: [],
	});

	// Detect changes by comparing current individual pieces with original
	useEffect(() => {
		if (isCreatingNew) {
			// For new analysts, no change detection needed
			setHasChanges(false);
			return;
		}

		if (!selectedAnalystData || isLoadingAnalystDetails) {
			setHasChanges(false);
			return;
		}

		// Extract current dataScope
		const currentDsInstructions = selectedAnalystData.data_scope?.instructions || "";
		const currentDsUseDefaultScope = selectedAnalystData.data_scope?.use_default_scope || false;
		const currentDsMetrics = (selectedAnalystData.data_scope?.metrics || [])
			.map(m => m.header?.id)
			.filter((id): id is string => !!id)
			.sort();
		const currentDsBenchmarks = (selectedAnalystData.data_scope?.benchmarks || [])
			.map(b => b.header?.id)
			.filter((id): id is string => !!id)
			.sort();
		const currentDsColumnScopes = (selectedAnalystData.data_scope?.column_scopes || [])
			.map(cs => ({
				id: cs?.kg_column?.header?.id,
				operator: cs.operator,
				values: cs.values ? [...cs.values].sort() : undefined,
				is_hidden: cs.is_hidden,
			}));

		// Compare each field - use lodash.isEqual for arrays
		const hasChanged =
			(selectedAnalystData.header?.name || "") !== originalDataRef.current.name ||
			(selectedAnalystData.header?.description || "") !== originalDataRef.current.description ||
			!lodash.isEqual(scenarios, originalDataRef.current.scenarios);

		// Check if data scope has changed
		const dataScopeChanged =
			currentDsInstructions !== originalDataRef.current.dsInstructions ||
			currentDsUseDefaultScope !== originalDataRef.current.dsUseDefaultScope ||
			!lodash.isEqual(currentDsMetrics, originalDataRef.current.dsMetrics) ||
			!lodash.isEqual(currentDsBenchmarks, originalDataRef.current.dsBenchmarks) ||
			!lodash.isEqual(currentDsColumnScopes, originalDataRef.current.dsColumnScopes);

		setHasDataScopeChanges(dataScopeChanged);

		setHasChanges(hasChanged || dataScopeChanged);

	}, [selectedAnalystData, scenarios, isCreatingNew, isLoadingAnalystDetails]);

	// Fetch jobs for all analysts when analysts are loaded
	useEffect(() => {
		const fetchAllAnalystJobs = async () => {
			if (!isLoadingAnalysts && analystsList.length > 0) {
				setIsLoadingAllJobs(true);
				setActiveAnalystsMap([]); // Reset before fetching
				try {
					// Fetch jobs for all analysts in parallel
					const jobPromises = analystsList.map(async (analyst) => {
						if (analyst.id) {
							const response = await listScheduledJobs(
								"SCENARIO_JOB",
								-1,
								1,
								analyst.id,
								"LAST_MODIFIED",
								false,
								true, /* includeScheduleActivity */
								"", /* activeSchedule */
							);
							return { analystId: analyst.id, jobs: response || [] };
						}
						return { analystId: analyst.id || "", jobs: [] };
					});

					const results = await Promise.all(jobPromises);

					// Build the map
					const newMap = new Map<string, Header[]>();
					results.forEach(({ analystId, jobs }) => {
						if (analystId) {
							newMap.set(analystId, jobs);
						}
					});

					setAnalystJobsMap(newMap);

					// Build active analysts map
					const activeAnalysts: {
						analystId: string;
						active: boolean;
						noOfActiveJobs: number;
						noOfTotalJobs: number;
					}[] = [];

					newMap.forEach((jobs, analystId) => {
						const activeJobs = jobs.filter(job => job.has_active_schedule);
						const totalJobs = jobs.length;
						activeAnalysts.push({
							analystId,
							active: activeJobs.length > 0,
							noOfActiveJobs: activeJobs.length,
							noOfTotalJobs: totalJobs
						});
					});

					setActiveAnalystsMap(activeAnalysts);
				} catch (error) {
					console.error("Error fetching all analyst jobs:", error);
				} finally {
					setIsLoadingAllJobs(false);
				}
			}
		};

		fetchAllAnalystJobs();
	}, [isLoadingAnalysts, analystsList]);

	// Handle analyst loading from URL parameter
	useEffect(() => {
		const loadAnalyst = async () => {
			if (id && analystsList.length > 0 && !selectedAnalyst) {
				// Handle loading existing analyst from URL
				const analyst = analystsList.find(analyst => analyst.id === id);
				if (analyst) {
					setIsLoadingAnalystDetails(true);
					setSelectedAnalyst(analyst);
					setIsCreatingNew(false);
					try {
						if (analyst?.id) {
							const analystData = await getAnalyst(analyst.id);
							setSelectedAnalystData(analystData || undefined);
							setOriginalAnalystData(analystData || undefined);
							setScenarios(analystData?.scenarios || []);

							// Store original data as individual pieces (arrays, not JSON strings)
							originalDataRef.current = {
								name: analystData?.header?.name || "",
								description: analystData?.header?.description || "",
								dsInstructions: analystData?.data_scope?.instructions || "",
								dsUseDefaultScope: analystData?.data_scope?.use_default_scope || false,
								dsMetrics: (analystData?.data_scope?.metrics || [])
									.map(m => m.header?.id)
									.filter((id): id is string => !!id)
									.sort(),
								dsBenchmarks: (analystData?.data_scope?.benchmarks || [])
									.map(b => b.header?.id)
									.filter((id): id is string => !!id)
									.sort(),
								dsColumnScopes: (analystData?.data_scope?.column_scopes || [])
									.map(cs => ({
										id: cs?.kg_column?.header?.id,
										operator: cs.operator,
										values: cs.values ? [...cs.values].sort() : undefined,
										is_hidden: cs.is_hidden,
									})),
								scenarios: analystData?.scenarios || [],
							};
						}
					} catch (error) {
						console.error("Error fetching analyst details:", error);
						setSelectedAnalystData(undefined);
						setOriginalAnalystData(undefined);
					} finally {
						setIsLoadingAnalystDetails(false);
					}
				}
			}
		};
		loadAnalyst();
	}, [id, analystsList, selectedAnalyst]);

	// Handle tab switching based on URL
	useEffect(() => {
		if (location.pathname.includes("/jobs")) {
			setIsTabActive(1);
		} else if (location.pathname.includes(`/analyst/${id}`)) {
			setIsTabActive(0);
		} else {
			handleBackToList();
		}
	}, [location.pathname]);

	// Handle analyst selection for editing
	const handleAnalystSelection = async (analyst: Header) => {
		setIsLoadingAnalystDetails(true);
		setSelectedAnalyst(analyst);
		setIsCreatingNew(false);
		// Update URL to include analyst ID
		if (analyst?.id) {
			navigate(`/analyst/${analyst.id}`, { replace: true });
		}

		// Switch to Config tab if currently on other tabs
		if (isTabActive !== 0) {
			setIsTabActive(0);
		}

		// Fetch analyst data
		try {
			if (analyst?.id) {
				const analystData = await getAnalyst(analyst.id);
				setSelectedAnalystData(analystData || undefined);
				setOriginalAnalystData(analystData || undefined);
				setScenarios(analystData?.scenarios || []);

				// Store original data as individual pieces (arrays, not JSON strings)
				originalDataRef.current = {
					name: analystData?.header?.name || "",
					description: analystData?.header?.description || "",
					dsInstructions: analystData?.data_scope?.instructions || "",
					dsUseDefaultScope: analystData?.data_scope?.use_default_scope || false,
					dsMetrics: (analystData?.data_scope?.metrics || [])
						.map(m => m.header?.id)
						.filter((id): id is string => !!id)
						.sort(),
					dsBenchmarks: (analystData?.data_scope?.benchmarks || [])
						.map(b => b.header?.id)
						.filter((id): id is string => !!id)
						.sort(),
					dsColumnScopes: (analystData?.data_scope?.column_scopes || [])
						.map(cs => ({
							id: cs?.kg_column?.header?.id,
							operator: cs.operator,
							values: cs.values ? [...cs.values].sort() : undefined,
							is_hidden: cs.is_hidden,
						})),
					scenarios: analystData?.scenarios || [],
				};
			}
		} catch (error) {
			console.error("Error fetching analyst details:", error);
			setSelectedAnalystData(undefined);
		} finally {
			setIsLoadingAnalystDetails(false);
		}
	};

	// Handle creating a new analyst
	const handleCreateNewAnalyst = () => {
		setSelectedAnalyst(null);
		setSelectedAnalystData(undefined);
		setOriginalAnalystData(undefined);
		setScenarios([]);
		setIsCreatingNew(true);
		setIsTabActive(0); // Ensure we're on Config tab
		setHasChanges(false);

		// Reset original data (empty arrays)
		originalDataRef.current = {
			name: "",
			description: "",
			dsInstructions: "",
			dsUseDefaultScope: true,
			dsMetrics: [],
			dsBenchmarks: [],
			dsColumnScopes: [],
			scenarios: [],
		};

		navigate("/analyst");
	};

	// Handle back to list
	const handleBackToList = () => {
		setSelectedAnalyst(null);
		setSelectedAnalystData(undefined);
		setOriginalAnalystData(undefined);
		setScenarios([]);
		setIsCreatingNew(false);
		setIsTabActive(0);
		setHasChanges(false);

		// Reset original data (empty arrays)
		originalDataRef.current = {
			name: "",
			description: "",
			dsInstructions: "",
			dsUseDefaultScope: true,
			dsMetrics: [],
			dsBenchmarks: [],
			dsColumnScopes: [],
			scenarios: [],
		};

		navigate("/analyst");
	};

	// Handle save analyst (create new or update existing)
	// Optional updatedScenarios param is used when called from scenario save (since state update is async)
	const handleSaveAnalyst = async (updatedScenarios?: ScenarioAnalysis[]) => {
		if (!selectedAnalystData) {
			showToast("No analyst data to save", "", "warning");
			return;
		}

		// Validate required fields
		if (!selectedAnalystData.header?.name || selectedAnalystData.header.name.trim() === "") {
			showToast("Please enter analyst name", "", "warning");
			return;
		}

		setIsSaving(true);
		try {
			if (isCreatingNew) {
				// Create new analyst
				const newAnalyst: AnalystProto = {
					...selectedAnalystData,
					header: {
						...selectedAnalystData.header,
						id: uuidv4(),
						type: MetadataTypeEnum.ANALYST,
						created_date_ms: Date.now(),
						last_modified_date_ms: Date.now(),
						last_modified_by: "current_user",
					},
					scenarios: [], // Initially empty
				};

				const result = await createAnalyst(newAnalyst);

				if (result && result.header) {
					showToast("Analyst created successfully", "", "success");

					// Add new analyst to top of list
					setAnalystsList(prev => [result.header!, ...prev]);

					// Select the newly created analyst
					setSelectedAnalyst(result.header);
					setSelectedAnalystData(result);
					setOriginalAnalystData(result);
					setIsCreatingNew(false);

					// Store as new original data (arrays, not JSON strings)
					originalDataRef.current = {
						name: result.header?.name || "",
						description: result.header?.description || "",
						dsInstructions: result.data_scope?.instructions || "",
						dsUseDefaultScope: result.data_scope?.use_default_scope || false,
						dsMetrics: (result.data_scope?.metrics || [])
							.map(m => m.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsBenchmarks: (result.data_scope?.benchmarks || [])
							.map(b => b.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsColumnScopes: (result.data_scope?.column_scopes || [])
							.map(cs => ({
								id: cs?.kg_column?.header?.id,
								operator: cs.operator,
								values: cs.values ? [...cs.values].sort() : undefined,
								is_hidden: cs.is_hidden,
							})),
						scenarios: result.scenarios || [],
					};
					setHasChanges(false);

					// Navigate to the saved analyst's URL
					if (result.header?.id) {
						navigate(`/analyst/${result.header.id}`, { replace: true });
					}
				}
			} else {
				// Update existing analyst
				const updatedAnalyst: AnalystProto = {
					...selectedAnalystData,
					header: {
						...selectedAnalystData.header,
						last_modified_date_ms: Date.now(),
						last_modified_by: "current_user",
					},
					scenarios: updatedScenarios ?? scenarios,
				};

				const result = await updateAnalyst(updatedAnalyst, hasDataScopeChanges);

				if (result && result.header) {
					showToast("Analyst updated successfully", "", "success");

					// Update the analyst in the list
					setAnalystsList(prev =>
						prev.map(h => h.id === result.header!.id ? result.header! : h)
					);

					// Update local state
					setSelectedAnalyst(result.header);
					setSelectedAnalystData(result);
					setOriginalAnalystData(result);
					setScenarios(result.scenarios || []);

					// Update original data to new saved state (arrays, not JSON strings)
					originalDataRef.current = {
						name: result.header?.name || "",
						description: result.header?.description || "",
						dsInstructions: result.data_scope?.instructions || "",
						dsUseDefaultScope: result.data_scope?.use_default_scope || false,
						dsMetrics: (result.data_scope?.metrics || [])
							.map(m => m.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsBenchmarks: (result.data_scope?.benchmarks || [])
							.map(b => b.header?.id)
							.filter((id): id is string => !!id)
							.sort(),
						dsColumnScopes: (result.data_scope?.column_scopes || [])
							.map(cs => ({
								id: cs?.kg_column?.header?.id,
								operator: cs.operator,
								values: cs.values ? [...cs.values].sort() : undefined,
								is_hidden: cs.is_hidden,
							})),
						scenarios: result.scenarios || [],
					};
					setHasChanges(false);

					if (hasDataScopeChanges && result.header?.id) {
						refreshAnalystDataScope(result.header.id, true);
					}
				}
			}
		} catch (error) {
			showToast("Failed to save analyst", "", "error");
			console.error("Error saving analyst:", error);
		} finally {
			setIsSaving(false);
		}
	};

	// Handle cancel or discard changes - reset to original data
	const handleCancel = () => {
		if (hasChanges && !isCreatingNew && selectedAnalystData) {
			if (originalAnalystData) {
				// Reset selectedAnalystData to original
				setSelectedAnalystData({ ...originalAnalystData });
				setScenarios(originalAnalystData.scenarios || []);
				setHasDiscardedChanges(true);
				showToast("Changes discarded", "", "info");
			} else {
				handleBackToList();
			}
		} else if (isCreatingNew) {
			// Go back to list for new analyst
			handleBackToList();
		}
	};

	// Handle tab change
	const handleTabChange = (index: number) => {
		setIsTabActive(index);
		if (index === 0) {
			// Navigate to configuration tab
			if (isCreatingNew) {
				// Need to do this because when we navigate back during creation,
				// then /analyst/undefined is shown which is not a valid URL
				navigate("/analyst");
			} else {
				navigate(`/analyst/${id}`);
			}
		} else {
			// Navigate to analyses tab
			navigate(`/analyst/${id}/jobs`);
		}
	};

	// Handle opening chat in new tab
	const handleOpenChat = () => {
		if (selectedAnalyst?.id) {
			window.open(`/chat?chatAnalystId=${selectedAnalyst.id}`, "_blank");
		}
	};

	const showFooterBar = (isCreatingNew || hasChanges) && !isViewingOrEditingJob;

	const renderTabs = () => (
		<Flex direction="column" height="100%" bg="white">
			<Tabs
				colorScheme="purple"
				variant="line"
				size="md"
				flex="1"
				minH={0}
				display="flex"
				flexDirection="column"
				defaultIndex={0}
				index={isTabActive}
				onChange={handleTabChange}
			>
				{/* Header with Back Button and Pill Tabs - hidden when on Scenario Creation Chat */}
				<HStack
					px={3}
					pt={2}
					pb={0.5}
					borderBottom="1px"
					borderColor="gray.200"
					justify="space-between"
					flexShrink={0}
				>
					{/* Left Side: Back Button + Analyst Name */}
					<HStack spacing={4}>
						{/* Back Button */}
						<Button
							leftIcon={<Icon as={FaArrowLeft} boxSize={4} />}
							variant="ghost"
							size="sm"
							onClick={handleBackToList}
							color="gray.700"
							_hover={{ bg: "gray.100" }}
							flexShrink={0}
							iconSpacing={0}
						/>

						{/* Analyst Name */}
						<HStack spacing={3}>
							<Icon as={BsRobot} boxSize={7} color="purple.700" />
							<Text
								fontSize="md"
								fontWeight="600"
								color="gray.800"
								mb={0}
								align="left"
								noOfLines={1}
							>
								{selectedAnalystData?.header?.name || (isCreatingNew ? "Creating New Analyst" : "Untitled Analyst")}
							</Text>
						</HStack>
					</HStack>
					<HStack>
						{/* Chat Button - Only show when not creating new */}
						{selectedAnalyst && !isCreatingNew && (
							<Button
								leftIcon={<Icon as={IoChatbubble} boxSize={4} />}
								onClick={handleOpenChat}
								size="sm"
								colorScheme="purple"
								bg="purple.700"
								_hover={{ bg: "purple.600" }}
								color="white"
								fontWeight="600"
								fontSize="md"
								mr={2}
							>
								Start Chat
							</Button>
						)}
						{/* Right Side: Underline-style Tabs */}
						<TabList border="none" gap={0} justifyContent="flex-end">
							<Tab
								fontSize="md"
								fontWeight="700"
								color="gray.400"
								_selected={{
									color: "purple.700",
									borderColor: "purple.700",
									borderBottomWidth: "3px",
								}}
								_hover={{ color: "purple.700" }}
							>
								<HStack spacing={2} whiteSpace="nowrap">
									<Icon as={FaGear} boxSize={5} />
									<Text mb={0}>Setup</Text>
								</HStack>
							</Tab>
							{/* Analyses and Schedules tabs - Only show when not creating new analyst */}
							{!isCreatingNew && (
								<Tab
									isDisabled={!selectedAnalyst || !selectedAnalyst.id}
									fontSize="md"
									fontWeight="700"
									color="gray.400"
									_selected={{
										color: "purple.700",
										borderColor: "purple.700",
										borderBottomWidth: "3px",
									}}
									_hover={{ color: "purple.700" }}
								>
									<HStack spacing={2}>
										<Icon as={MdAnalytics} boxSize={6} />
										<Text mb={0}>Analyses</Text>
									</HStack>
								</Tab>
							)}
						</TabList>

					</HStack>
				</HStack>

				<TabPanels flex="1" minH={0} overflow="auto" bg="white">
					{/* Configuration Tab */}
					<TabPanel height="100%" p={0}>
						<Box height="100%">
							<AnalystDataAccessComponent
								analyst={selectedAnalystData}
								setAnalyst={setSelectedAnalystData}
								isCreatingNew={isCreatingNew}
								hasDiscardedChanges={hasDiscardedChanges}
								setHasDiscardedChanges={setHasDiscardedChanges}
								originalAnalyst={originalAnalystData}
							/>
						</Box>
					</TabPanel>
					<TabPanel height="100%" p={0}>
						<AnalystJobsComponent
							analystId={selectedAnalyst?.id}
							setIsViewingOrEditingJob={setIsViewingOrEditingJob}
							analystJobsMap={analystJobsMap}
							setAnalystJobsMap={setAnalystJobsMap}
							initialJobId={jobId}
						/>
					</TabPanel>
				</TabPanels>
			</Tabs>

			{/* Footer action bar - Show when creating/editing analyst AND NOT creating/editing scenario/job AND NOT on scenario creation chat */}
			{showFooterBar && (
				<Flex
					flexShrink={0}
					borderTop="1px"
					borderColor="gray.200"
					py={2}
					px={6}
					bg="white"
					align="center"
					justify="space-between"
					gap={3}
				>
					{/* Left: Unsaved changes message */}
					{hasChanges && (
						<Text fontSize="md" color="gray.700" fontWeight="600" mb={0}>
							You have unsaved changes.
						</Text>
					)}
					{!hasChanges && <Box flex="1" />}

					{/* Right: Action buttons */}
					<HStack spacing={3}>
						{/* Save/Update Button - Only show when creating or has changes */}
						{(isCreatingNew || hasChanges) && (
							<Button
								colorScheme="green"
								size="md"
								onClick={() => handleSaveAnalyst()}
								isLoading={isSaving}
								loadingText={isCreatingNew ? "Saving..." : "Updating..."}
								leftIcon={<Icon as={FaCheck} boxSize={5} />}
								fontWeight="600"
								transition="all 0.2s"
								fontSize="md"
							>
								{isCreatingNew ? "Save" : "Update"}
							</Button>
						)}

						{/* Cancel/Discard Button - Only show when creating or has changes */}
						{(isCreatingNew || hasChanges) && (
							<Button
								colorScheme="red"
								variant="outline"
								size="md"
								onClick={handleCancel}
								isDisabled={isSaving}
								leftIcon={<Icon as={FaUndo} boxSize={5} />}
								fontWeight="600"
								fontSize="md"
							>
								{isCreatingNew ? "Cancel" : "Discard"}
							</Button>
						)}
					</HStack>
				</Flex>
			)}
		</Flex>
	);

	const renderMainContent = () => {
		if (isLoadingAnalystDetails) {
			return (
				<Center height="100%">
					<VStack spacing={4}>
						<Spinner size="xl" color="purple.600" thickness="3px" />
						<Text color="gray.600" fontSize="md">Loading. Please hold on...</Text>
					</VStack>
				</Center>
			);
		}

		if (selectedAnalystData || isCreatingNew) {
			return renderTabs();
		}

		return null; // Grid view will be shown instead
	};

	return (
		<Box width="100%" height="100vh" bg="gray.50">
			{selectedAnalystData || isCreatingNew || isLoadingAnalystDetails ? (
				// Show main content when analyst is selected or creating new
				<Box width="100%" height="100%">
					{renderMainContent()}
				</Box>
			) : (
				// Show grid view when no analyst is selected
				<Box width="100%" height="100%">
					<ListAnalysts
						analystHeaders={analystsList}
						setAnalystHeaders={setAnalystsList}
						selectedAnalyst={selectedAnalyst}
						onAnalystSelection={handleAnalystSelection}
						onCreateNewAnalyst={handleCreateNewAnalyst}
						onBackToList={handleBackToList}
						isLoading={isLoadingAnalysts}
						isLoadingAllJobs={isLoadingAllJobs}
						activeAnalystsMap={activeAnalystsMap}
					/>
				</Box>
			)}
		</Box>
	);
};

export default AnalystPage;
